import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import SubscriptionDialog from '../components/SubscriptionDialog';
import { getCurrentSubscription } from '../services/api';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children, onNavigate }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [modelName, setModelName] = useState('');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onAlternativeSelect, setOnAlternativeSelect] = useState(null);

  const updateSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCurrentSubscription();
      setCurrentSubscription(data.subscription);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    updateSubscription();
  }, [updateSubscription]);

  const showSubscriptionDialog = (model = '', onAlternativeCallback = null) => {
    setModelName(model);
    setOnAlternativeSelect(() => onAlternativeCallback);
    setDialogOpen(true);
  };

  const hideSubscriptionDialog = () => {
    setDialogOpen(false);
    setModelName('');
    setOnAlternativeSelect(null);
  };

  const handleSubscribe = () => {
    hideSubscriptionDialog();
    onNavigate('/settings?active_tab=billing');
  };

  const handleAlternativeSelect = (alternativeModel) => {
    if (onAlternativeSelect) {
      onAlternativeSelect(alternativeModel);
    }
    hideSubscriptionDialog();
  };

  return (
    <SubscriptionContext.Provider 
      value={{ 
        showSubscriptionDialog,
        currentSubscription,
        loading,
        updateSubscription,
        isActive: currentSubscription?.status === 'active'
      }}
    >
      {children}
      <SubscriptionDialog
        open={dialogOpen}
        onClose={hideSubscriptionDialog}
        modelName={modelName}
        onSubscribe={handleSubscribe}
        onSelectAlternative={handleAlternativeSelect}
      />
    </SubscriptionContext.Provider>
  );
}; 